import axios from 'axios'

import { SynthContextOptions, SynthesisContextType } from '@utils/nf-webaudio/synthesis-context'

import { InstrumentDescriptorType } from './instrument-descriptor'
import nfsyn from './nf-webaudio'

/**
 * Parsing and data representation for a Noteflight instrument manifest.
 *
 * TODO: If the manifest was JSON instead of XML, then these classes can nearly go away.  Almost no parsing will be necessary.
 */
type InstrumentList = {
  list: InstrumentDescriptorType[]
  map: InstrumentMap
  groupsList: []
}

type InstrumentMap = Record<string, InstrumentDescriptorType>

export interface InstrumentManifestType {
  synth: SynthesisContextType
  options: SynthContextOptions
  premium: InstrumentList
  basic: InstrumentList
  loaded: boolean
  _parsedGroupsList: boolean
  parseManifest: (Element) => void
  promise: () => Promise<void>
  getInstrumentByUri: (premium: boolean, uri: string) => InstrumentDescriptorType
}

const InstrumentManifest = function (
  this: InstrumentManifestType,
  synth: SynthesisContextType,
  options: SynthContextOptions
): void {
  this.synth = synth
  this.options = options

  // For each collection of instruments, maintain both an ordered list and a map from URIs to instruments.
  this.premium = { list: [], map: {}, groupsList: [] }
  this.basic = { list: [], map: {}, groupsList: [] }
  this.loaded = false
  this._parsedGroupsList = false
}

InstrumentManifest.prototype.promise = function (this: InstrumentManifestType) {
  return new Promise((resolve, reject) => {
    const manifestUrl = this.options.manifestLocation

    axios
      .get(manifestUrl, {
        headers: { 'Content-Type': 'text/xml' },
      })
      .then(({ data }) => {
        this.parseManifest(data)
        resolve(null)
      })
      .catch(() => {
        // TODO: error reporting should be handled by UI code, not here.
        alert('Could not load instrument manifest at ' + manifestUrl)
        reject()
      })
  })
}

InstrumentManifest.prototype.parseManifest = function (this: InstrumentManifestType, xml: string) {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(xml, 'text/xml')

  // spread operator converts type HTMLCollectionOf<Element> to Element[]
  // so that it can be looped over
  const instrumentLibraries = [...xmlDoc.getElementsByTagName('instrumentLibrary')]

  instrumentLibraries.forEach((library) => {
    const instruments: InstrumentList =
      library.getAttribute('premium') === 'true' ? this.premium : this.basic
    const instrumentXMLElements = [...library.getElementsByTagName('instrument')]
    instrumentXMLElements.forEach((instrumentXml) => {
      const descriptor: InstrumentDescriptorType = new nfsyn.InstrumentDescriptor(
        this,
        instrumentXml
      )
      instruments.list.push(descriptor)
      instruments.map[descriptor.getUri()] = descriptor
      const aliases = descriptor.getAliases()
      for (let i = 0; i < aliases.length; i++) {
        instruments.map[aliases[i]] = descriptor
      }
    })
  })
}

InstrumentManifest.prototype.getInstrumentByUri = function (
  this: InstrumentManifestType,
  premium: boolean,
  uri: string
): InstrumentDescriptorType {
  const instruments = premium ? this.premium : this.basic
  const map: InstrumentMap = instruments.map

  // Look for a match, stripping off trailing components of the URI until a match is found
  const newLocal = true
  while (newLocal) {
    if (map[uri]) {
      return map[uri]
    }
    const slashIndex = uri.lastIndexOf('/')
    if (slashIndex < 0) {
      break
    }
    uri = uri.substring(0, slashIndex)
  }

  return map['default']
}

export default InstrumentManifest
